import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTimes } from '@fortawesome/free-solid-svg-icons';
import { pullAt, uniqueId } from 'lodash';
import { useMemo } from 'react';

import Button from '../Button';
import RoomFilterInput from './RoomFilterInput';
import Tooltip from '../../utils/Tooltip';

import type { ActionMeta, OnChangeValue } from 'react-select/dist/declarations/src/types';
import type { EditableRoomFilter } from '../../../../types';
import type { Option } from './RoomFilterInput';
import type { ReactNode } from 'react';

const ROOM_FILTER_INPUT_NAME_PREFIX = 'room-filter-';

const createEmptyRoomFilter = (): EditableRoomFilter => ({
  room_filter_expressions: [],
});

interface Props {
  className?: string;
  filters: EditableRoomFilter[];
  isDisabled?: boolean;
  helperText?: ReactNode;
  id?: string;
  label?: string;
  name?: string;
  onChange: (newValue: EditableRoomFilter[], name?: string) => void;
}

const RoomFiltersBuilder = ({
  className,
  filters,
  helperText,
  id,
  isDisabled = false,
  label,
  name,
  onChange,
}: Props) => {
  id = useMemo(() => id || uniqueId('room-filters-builder-'), [id]);

  const handleAddRule = () => {
    onChange([
      ...filters,
      createEmptyRoomFilter(),
    ], name);
  };

  const handleRemoveRule = (i: number) => {
    const filtersCopy = filters.slice();
    pullAt(filtersCopy, [i]);
    onChange(filtersCopy, name);
  };

  const handleFilterChange = (options: OnChangeValue<Option, true>, actionMeta: ActionMeta<Option>) => {
    const index = parseInt(actionMeta.name?.replace(ROOM_FILTER_INPUT_NAME_PREFIX, '') || '-1', 10);
    const selectedFilter = {
      room_filter_expressions: (options ?
        options.map((option) => ({
          filterable_id: option.filterable_id,
          filterable_type: option.filterable_type,
          negated: option.negated,
        })) :
        []
      ),
    };
    onChange(filters.map((filter, i) => (i === index ? selectedFilter : filter)), name);
  };

  return (
    <div className={`input room-filters-builder${className ? ` ${className}` : ''}`}>
      {label && <label htmlFor={id}>{label}</label>}
      {filters.map((filter, i) => (
        <div
          className="filter-input-container"
          key={`filter-input-${i}`}
        >
          {i > 0 && <span className="filter-separator">or</span>}
          <RoomFilterInput
            isDisabled={isDisabled}
            name={`room-filter-${i}`}
            onChange={handleFilterChange}
            selectedFilterExpressions={filter.room_filter_expressions}
          />
          {!isDisabled && filters.length > 1 &&
            <Button
              className="btn-delete"
              color="no-outline"
              iconRight={<FontAwesomeIcon icon={faTimes} />}
              onClick={() => handleRemoveRule(i)}
              size="small"
              tooltip={
                <Tooltip
                  id={`remove-room-filter-${i}-button`}
                  position="top"
                  value="Remove"
                />
              }
            />
          }
        </div>
      ))}
      {helperText && <div className="helper-text">{helperText}</div>}
      {!isDisabled &&
        <Button
          className="btn-add-rule"
          color="no-outline"
          iconLeft={<FontAwesomeIcon icon={faPlus} />}
          onClick={handleAddRule}
          size="small"
          value="Add Alternate Rule"
        />
      }
    </div>
  );

};

export default RoomFiltersBuilder;
